/**
 * nbn-cart-pdf.js
 * Nine by Nine Golf — Cart PDF Export  v2
 */

(function () {
  'use strict';

  const IRON_ORDER = ['1i','2i','3i','4i','5i','6i','7i','8i','9i','PW','GW','AW','SW','LW','W'];

  // ── Properties hidden from the PDF, matching the HTML cart exactly. ──
  // The HTML only hides: underscore-prefixed keys, iron_groupset_id, and
  // iron_sequence. Everything else (Top Line SKU, Loft, Lie, Shaft, Grip,
  // Ferrule, Notes, etc) is shown on the Main row.
  const HIDDEN_PROP_KEYS = new Set([
    'iron_groupset_id',
    'iron_sequence',
  ]);

  // Main-row property list: mirrors the HTML main-row filter exactly.
  function getMainProps(item) {
    return Object.entries(item.properties || {}).filter(([k, v]) => {
      if (!v || v === '') return false;
      if (k.startsWith('_')) return false;
      if (HIDDEN_PROP_KEYS.has(k)) return false;
      return true;
    });
  }

  function money(pence) {
    // Format with thousand separators: 138300 -> £1,383.00
    const pounds = (pence / 100).toFixed(2);
    const parts = pounds.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '\u00A3' + parts.join('.');
  }

  // Matches the HTML child row exactly: variant options first, then every
  // line-item property whose key doesn't start with "_". This includes
  // "For:", "Ferrule:", etc — same as what the customer sees on the page.
  function getChildDisplayLines(item) {
    const lines = [];
    // Variant options (skip "Default Title")
    (item.options_with_values || []).forEach(o => {
      if (o.value && o.value !== 'Default Title') {
        lines.push([o.name, o.value]);
      }
    });
    // Properties — everything not starting with "_"
    Object.entries(item.properties || {}).forEach(([k, v]) => {
      if (!v || v === '') return;
      if (k.startsWith('_')) return;
      lines.push([k, String(v)]);
    });
    return lines;
  }

  function groupCartItems(items) {
    const childrenByParentId = {};
    const childKeys = new Set();

    items.forEach(item => {
      const pid = item.properties && item.properties._component_parent_id;
      if (pid) {
        if (!childrenByParentId[pid]) childrenByParentId[pid] = [];
        childrenByParentId[pid].push(item);
        childKeys.add(item.key);
      }
    });

    const ironGroupsets = {};
    const standaloneGroups = [];

    items.forEach(item => {
      if (childKeys.has(item.key)) return;
      const gsId = item.properties && item.properties.iron_groupset_id;
      if (gsId) {
        if (!ironGroupsets[gsId]) ironGroupsets[gsId] = [];
        ironGroupsets[gsId].push(item);
      } else {
        const cid = item.properties && item.properties._component_id;
        standaloneGroups.push({
          parent: item,
          children: cid ? (childrenByParentId[cid] || []) : [],
        });
      }
    });

    const ironSuperGroups = Object.entries(ironGroupsets).map(([gsId, mains]) => {
      const sorted = [...mains].sort((a, b) => {
        const getIron = item => {
          const o = (item.options_with_values || []).find(x => x.name && x.name.toLowerCase() === 'iron');
          return o ? o.value : '';
        };
        return IRON_ORDER.indexOf(getIron(a)) - IRON_ORDER.indexOf(getIron(b));
      });
      return {
        gsId,
        mains: sorted.map(m => ({
          main: m,
          children: childrenByParentId[m.properties._component_id] || [],
        })),
      };
    });

    return { standaloneGroups, ironSuperGroups };
  }

  function buildPDF(cart, imageMap) {
    imageMap = imageMap || {};
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const PW = doc.internal.pageSize.getWidth();
    const PH = doc.internal.pageSize.getHeight();
    const M = 14;          // margin
    const CW = PW - M * 2; // content width
    const FOOTER_H = 10;   // reserved footer height
    const SAFE_BOTTOM = PH - M - FOOTER_H;

    // ── Colour palette ──
    const C = {
      accent:     [34, 100, 107],
      accentLt:   [232, 244, 245],
      accentBdr:  [184, 220, 224],
      black:      [26, 26, 23],
      muted:      [107, 104, 96],
      mutedLt:    [156, 152, 144],
      surface:    [255, 255, 255],
      surface2:   [249, 248, 246],
      light:      [244, 243, 241],
      border:     [228, 225, 219],
      danger:     [220, 80, 60],
    };

    let y = M;

    // ── Helpers ──
    const sf = (style, size, color) => {
      doc.setFont('helvetica', style);
      doc.setFontSize(size);
      doc.setTextColor(...(color || C.black));
    };

    const needBreak = (h) => {
      if (y + h > SAFE_BOTTOM) {
        addPage();
        return true;
      }
      return false;
    };

    // Draw a product thumbnail with a bordered rounded frame. Falls back to
    // an empty framed box if the image failed to preload.
    const drawThumb = (imgUrl, tx, ty, size) => {
      // Frame
      doc.setFillColor(...C.surface2);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.2);
      doc.roundedRect(tx, ty, size, size, 1, 1, 'FD');
      const rec = imgUrl && imageMap[imgUrl];
      if (rec && rec.data) {
        // Fit image inside the frame preserving aspect ratio, with 0.5mm pad
        const pad = 0.6;
        const boxW = size - pad * 2;
        const boxH = size - pad * 2;
        const ar = rec.w / rec.h;
        let dw = boxW, dh = boxW / ar;
        if (dh > boxH) { dh = boxH; dw = boxH * ar; }
        const dx = tx + pad + (boxW - dw) / 2;
        const dy = ty + pad + (boxH - dh) / 2;
        try {
          doc.addImage(rec.data, 'JPEG', dx, dy, dw, dh);
        } catch (e) { /* ignore draw errors */ }
      }
    };

    const addPage = () => {
      drawFooter();
      doc.addPage();
      y = M;
      drawPageChrome(false);
    };

    const drawPageChrome = (isFirstPage) => {
      // Top teal rule
      doc.setFillColor(...C.accent);
      doc.rect(0, 0, PW, 1.8, 'F');

      var logo = imageMap[NBN_LOGO_URL];

      if (isFirstPage && logo && logo.data) {
        // ── First page: real Nine by Nine Golf logo (top-left) ──
        var maxLogoH = 12;              // mm — header logo height
        var logoH = maxLogoH;
        var logoW = logoH * (logo.w / logo.h);
        var maxLogoW = 55;              // cap width so wide logos don't sprawl
        if (logoW > maxLogoW) {
          logoW = maxLogoW;
          logoH = logoW * (logo.h / logo.w);
        }
        try {
          doc.addImage(logo.data, 'PNG', M, 5, logoW, logoH);
        } catch (e) { /* ignore draw errors */ }
      } else if (isFirstPage) {
        // First page but logo failed to load → text wordmark fallback.
        sf('bold', 11, C.black);
        doc.text('NINE BY NINE', M, 9);
        sf('normal', 6.5, C.muted);
        doc.text('GOLF', M, 13);
      }
      // Subsequent pages: no logo — keep the header clean (just the rule + URL).

      // Brand URL top-right (all pages)
      sf('normal', 8, C.muted);
      doc.text('ninebyninegolf.com', PW - M, 8, { align: 'right' });
    };

    const drawFooter = () => {
      // Only the divider line is drawn here during page transitions.
      // Page numbers ("Page X of Y") + URL are stamped in a single final
      // loop after all pages exist, so the total count is accurate and
      // nothing gets drawn twice.
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.2);
      doc.line(M, PH - FOOTER_H, PW - M, PH - FOOTER_H);
    };

    // ── CARD: top-level accordion group header ──
    const drawGroupCard = ({ title, tags, total, subLabel, isIronSet, imgUrl, indent = 0 }) => {
      const CARD_H = 15;
      needBreak(CARD_H + 2);

      const x = M + indent;
      const w = CW - indent;

      doc.setFillColor(...C.surface);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, y, w, CARD_H, 1.5, 1.5, 'FD');

      // Left accent bar
      doc.setFillColor(...C.accent);
      doc.roundedRect(x, y, 2.5, CARD_H, 0.8, 0.8, 'F');

      // Thumbnail
      const thumbSize = 11;
      drawThumb(imgUrl, x + 4, y + (CARD_H - thumbSize) / 2, thumbSize);

      let tx = x + 4 + thumbSize + 3;

      // Title row — pill (if iron set) then title
      let titleY = y + 6;
      if (isIronSet) {
        doc.setFillColor(...C.accentLt);
        doc.setDrawColor(...C.accentBdr);
        doc.setLineWidth(0.2);
        doc.roundedRect(tx, y + 3, 17, 5.5, 1, 1, 'FD');
        sf('bold', 6.5, C.accent);
        doc.text('IRON SET', tx + 8.5, y + 6.8, { align: 'center' });
        tx += 20;
      }

      sf('bold', 10.5, C.black);
      doc.text(title, tx, titleY);

      // Tags row — under the title, aligned with thumbnail's right edge
      if (tags && tags.length) {
        const tagStartX = x + 4 + thumbSize + 3;
        sf('normal', 7.5, C.muted);
        const tagLine = tags.join('   \u00B7   ');
        // Wrap if too long for available width
        const maxTagW = w - (tagStartX - x) - 34;
        const wrapped = doc.splitTextToSize(tagLine, maxTagW);
        doc.text(wrapped[0], tagStartX, y + 11);
      }

      // Right: total + sub-label
      sf('bold', 10.5, C.black);
      doc.text(total, PW - M - 2, y + 6.5, { align: 'right' });
      if (subLabel) {
        sf('normal', 7.5, C.muted);
        doc.text(subLabel, PW - M - 2, y + 11, { align: 'right' });
      }

      y += CARD_H + 2;
    };

    // ── Iron sub-group header ──
    const drawIronSubHeader = ({ ironLabel, name, tags, total }) => {
      const H = 10;
      needBreak(H + 1);

      doc.setFillColor(238, 248, 249);
      doc.setDrawColor(...C.accentBdr);
      doc.setLineWidth(0.25);
      doc.roundedRect(M + 4, y, CW - 4, H, 1, 1, 'FD');

      // Iron circle badge
      doc.setFillColor(...C.accent);
      doc.circle(M + 11, y + 5, 4, 'F');
      sf('bold', 7, [255,255,255]);
      doc.text(ironLabel, M + 11, y + 6.3, { align: 'center' });

      sf('bold', 9, C.black);
      doc.text(name, M + 18, y + 5.2);

      if (tags && tags.length) {
        sf('normal', 7, C.muted);
        doc.text(tags.join('  ·  '), M + 18, y + 8.5);
      }

      sf('bold', 9.5, C.black);
      doc.text(total, PW - M - 3, y + 5.2, { align: 'right' });

      y += H + 1.5;
    };

    // ── Main row (club head) ──
    const drawMainRow = (item, indent = 8) => {
      const props = getMainProps(item);
      const lineH = 4.2;
      const thumbSize = 10;
      const MIN_H = Math.max(14, thumbSize + 5);
      const propsH = props.length * lineH;
      const H = Math.max(MIN_H, 11 + propsH);

      needBreak(H + 2);

      const x = M + indent;
      const w = CW - indent;
      const priceW = 28; // right column width

      doc.setFillColor(...C.surface2);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.2);
      doc.rect(x, y, w, H, 'FD');

      // Thumbnail
      drawThumb(item.image, x + 3, y + 3, thumbSize);

      const infoX = x + 3 + thumbSize + 3;

      // Product title (no "MAIN" badge)
      sf('bold', 9, C.black);
      doc.text(item.product_title || item.title, infoX, y + 5.8);

      // Properties
      if (props.length) {
        props.forEach(([k, v], i) => {
          const label = `${k}: `;
          sf('normal', 7.5, C.muted);
          doc.text(label, infoX, y + 10.5 + i * lineH);
          const valueX = infoX + doc.getTextWidth(label);
          sf('normal', 7.5, C.black);
          doc.text(String(v), valueX, y + 10.5 + i * lineH);
        });
      }

      // Price (right column)
      sf('bold', 9.5, C.black);
      doc.text(money(item.final_line_price), x + w - 2, y + 5.8, { align: 'right' });
      if (item.original_line_price !== item.final_line_price) {
        sf('normal', 7.5, C.mutedLt);
        // strikethrough effect
        const origStr = money(item.original_line_price);
        const origX = x + w - 2 - doc.getTextWidth(origStr);
        doc.text(origStr, x + w - 2, y + 10, { align: 'right' });
        doc.setDrawColor(...C.mutedLt);
        doc.setLineWidth(0.3);
        doc.line(origX, y + 9.3, x + w - 2, y + 9.3);
      }

      y += H + 2;
    };

    // ── Child component row ──
    const drawChildRow = (item, indent = 10) => {
      const lines = getChildDisplayLines(item);
      // Build discount lines - clean Shopify auto-generated internal titles
      const discounts = (item.discounts || []).map(d => {
        let title = d.title
          .replace(/_[a-z_]+/gi, '')
          .replace(/\s*1\+\s*items?/gi, '')
          .replace(/\s{2,}/g, ' ')
          .trim();
        if (!title || title === 'off') title = 'Discount applied';
        return `${title}  (-${money(d.amount)})`;
      });
      const lineH = 4;
      const thumbSize = 9;
      const contentH = 7 + lines.length * lineH + (discounts.length ? discounts.length * lineH + 1 : 0);
      const H = Math.max(thumbSize + 4, contentH);

      needBreak(H + 1);

      const x = M + indent;
      const w = CW - indent;

      doc.setFillColor(252, 253, 253);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.15);
      doc.rect(x, y, w, H, 'FD');

      // Left indent guide line
      doc.setDrawColor(...C.accentBdr);
      doc.setLineWidth(0.6);
      doc.line(x + 2, y + 1, x + 2, y + H - 1);
      doc.setLineWidth(0.15);

      // Thumbnail
      drawThumb(item.image, x + 4, y + 2.5, thumbSize);
      const infoX = x + 4 + thumbSize + 3;

      // Product name
      sf('bold', 8.5, C.black);
      doc.text(item.product_title || item.title, infoX, y + 5.5);

      // Display lines (variant options + properties)
      let textY = y + 9.5;
      lines.forEach(([k, v]) => {
        sf('normal', 7, C.muted);
        doc.text(`${k}: `, infoX, textY);
        sf('normal', 7, C.black);
        doc.text(String(v), infoX + doc.getTextWidth(`${k}: `), textY);
        textY += lineH;
      });

      // Discounts — clean pill badges
      if (discounts.length) {
        textY += 0.5;
        discounts.forEach(line => {
          sf('bold', 7, C.accent);
          const badgeW = doc.getTextWidth(line) + 6;
          doc.setFillColor(...C.accentLt);
          doc.roundedRect(infoX, textY - 3, badgeW, 4.5, 0.5, 0.5, 'F');
          doc.text(line, infoX + 3, textY);
          textY += lineH;
        });
      }

      // Price — final, with original struck through
      sf('bold', 8.5, C.black);
      doc.text(money(item.final_line_price), PW - M - 3, y + 5.5, { align: 'right' });
      if (item.original_line_price !== item.final_line_price) {
        const origStr = money(item.original_line_price);
        const origX = PW - M - 3 - doc.getTextWidth(origStr);
        sf('normal', 7, C.mutedLt);
        doc.text(origStr, PW - M - 3, y + 9.5, { align: 'right' });
        doc.setDrawColor(...C.mutedLt);
        doc.setLineWidth(0.3);
        doc.line(origX, y + 8.8, PW - M - 3, y + 8.8);
        doc.setLineWidth(0.15);
      }

      y += H + 1;
    };

    // ── Simple single-product row (no components) ──
    // Mirrors the flat, non-accordion product row in the HTML cart. Used for
    // standalone products like "Fresh Ferrule" that have no child parts.
    const drawSimpleRow = (item) => {
      const tags = (item.options_with_values || [])
        .filter(o => o.value && o.value !== 'Default Title')
        .map(o => [o.name, o.value]);
      Object.entries(item.properties || {}).forEach(([k, v]) => {
        if (!v || v === '') return;
        if (k.startsWith('_')) return;
        if (HIDDEN_PROP_KEYS.has(k)) return;
        tags.push([k, String(v)]);
      });

      const lineH = 4.2;
      const thumbSize = 12;
      const H = Math.max(thumbSize + 6, 11 + tags.length * lineH);
      needBreak(H + 2);

      const x = M;
      const w = CW;

      doc.setFillColor(...C.surface);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, y, w, H, 1.5, 1.5, 'FD');

      // Left accent bar
      doc.setFillColor(...C.accent);
      doc.roundedRect(x, y, 2.5, H, 0.8, 0.8, 'F');

      // Thumbnail
      drawThumb(item.image, x + 4, y + (H - thumbSize) / 2, thumbSize);
      const infoX = x + 4 + thumbSize + 3;

      // Title
      sf('bold', 10, C.black);
      doc.text(item.product_title || item.title, infoX, y + 6);

      // Property lines
      tags.forEach(([k, v], i) => {
        sf('normal', 7.5, C.muted);
        doc.text(`${k}: `, infoX, y + 11 + i * lineH);
        sf('normal', 7.5, C.black);
        doc.text(String(v), infoX + doc.getTextWidth(`${k}: `), y + 11 + i * lineH);
      });

      // Price
      sf('bold', 10.5, C.black);
      doc.text(money(item.final_line_price), PW - M - 3, y + 6, { align: 'right' });
      if (item.original_line_price !== item.final_line_price) {
        const origStr = money(item.original_line_price);
        const origX = PW - M - 3 - doc.getTextWidth(origStr);
        sf('normal', 7.5, C.mutedLt);
        doc.text(origStr, PW - M - 3, y + 10.5, { align: 'right' });
        doc.setDrawColor(...C.mutedLt);
        doc.setLineWidth(0.3);
        doc.line(origX, y + 9.8, PW - M - 3, y + 9.8);
      }

      y += H + 1;
    };

    // ─────────────────────────────────────────────
    // BUILD DOCUMENT
    // ─────────────────────────────────────────────
    drawPageChrome(true);

    // ── Document header ──
    // Start below the first-page logo (logo occupies ~y5–17).
    y = 24;
    // Title row: "Your Cart" + item count on left, date on right
    sf('bold', 18, C.black);
    doc.text('YOUR CART', M, y);
    const titleW = doc.getTextWidth('YOUR CART');

    sf('normal', 10, C.muted);
    const countStr = `  ${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
    doc.text(countStr, M + titleW, y);

    sf('normal', 8, C.mutedLt);
    doc.text(
      new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      PW - M,
      y,
      { align: 'right' }
    );

    y += 4;
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.3);
    doc.line(M, y, PW - M, y);
    y += 7;

    // ── Render groups ──
    const { standaloneGroups, ironSuperGroups } = groupCartItems(cart.items);

    standaloneGroups.forEach(({ parent, children }) => {
      // A standalone product with NO components is a plain single line item.
      // It should render as one flat row — not an accordion group card +
      // Main row — matching how the HTML cart shows it.
      if (children.length === 0) {
        drawSimpleRow(parent);
        y += 3;
        return;
      }

      const total = parent.final_line_price + children.reduce((s, c) => s + c.final_line_price, 0);
      const tags = (parent.options_with_values || [])
        .filter(o => o.value && o.value !== 'Default Title')
        .map(o => o.value);
      const notes = parent.properties && parent.properties.Notes;
      if (notes) tags.push(`Notes: ${notes}`);

      drawGroupCard({
        title: parent.product_title || parent.title,
        tags,
        total: money(total),
        subLabel: `${children.length + 1} components`,
        isIronSet: false,
        imgUrl: parent.image,
      });
      drawMainRow(parent);
      children.forEach(c => drawChildRow(c));
      y += 3;
    });

    ironSuperGroups.forEach(({ mains }) => {
      if (!mains.length) return;
      const firstMain = mains[0].main;

      const superTotal = mains.reduce(
        (s, { main, children }) =>
          s + main.final_line_price + children.reduce((cs, c) => cs + c.final_line_price, 0),
        0
      );

      const ironNums = mains.map(({ main }) => {
        const o = (main.options_with_values || []).find(x => x.name && x.name.toLowerCase() === 'iron');
        return o ? o.value : '';
      }).filter(Boolean);

      const dex = (() => {
        const o = (firstMain.options_with_values || []).find(x => x.name && x.name.toLowerCase() === 'dexterity');
        return o ? o.value : '';
      })();

      const notes = firstMain.properties && firstMain.properties.Notes;
      // Build tags as individual items to match the HTML tag pills:
      // dexterity, then each iron number, then Notes
      const tags = [];
      if (dex) tags.push(dex);
      ironNums.forEach(n => tags.push(n));
      if (notes) tags.push(`Notes: ${notes}`);

      drawGroupCard({
        title: firstMain.product_title || 'Iron Set',
        tags,
        total: money(superTotal),
        subLabel: `${mains.length} iron${mains.length !== 1 ? 's' : ''}`,
        isIronSet: true,
        imgUrl: firstMain.image,
      });

      mains.forEach(({ main, children }) => {
        const ironOpt = (main.options_with_values || []).find(x => x.name && x.name.toLowerCase() === 'iron');
        const ironLabel = ironOpt ? ironOpt.value : '';
        const dexOpt = (main.options_with_values || []).find(x => x.name && x.name.toLowerCase() === 'dexterity');
        const dexVal = dexOpt ? dexOpt.value : '';
        const subTotal = main.final_line_price + children.reduce((s, c) => s + c.final_line_price, 0);

        drawIronSubHeader({
          ironLabel,
          name: main.product_title || main.title,
          tags: [dexVal, `Iron: ${ironLabel}`].filter(Boolean),
          total: money(subTotal),
        });
        drawMainRow(main, 10);
        children.forEach(c => drawChildRow(c, 12));
        y += 1.5;
      });

      y += 3;
    });

    // ── Summary footer card ──
    const SUMM_H = 18;
    needBreak(SUMM_H + 4);
    y += 2;

    doc.setFillColor(...C.light);
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.3);
    doc.roundedRect(M, y, CW, SUMM_H, 2, 2, 'FD');

    // Total row
    const totalY = y + 8;
    sf('bold', 13, C.black);
    doc.text('Total', M + 5, totalY);
    sf('bold', 15, C.accent);
    doc.text(money(cart.total_price), PW - M - 5, totalY, { align: 'right' });

    // Policy note
    sf('normal', 7.5, C.muted);
    doc.text(
      'Tax included. Shipping calculated at checkout.',
      PW / 2,
      totalY + 6,
      { align: 'center' }
    );

    y += SUMM_H + 4;

    // ── Finalise all page footers ──
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.2);
      doc.line(M, PH - FOOTER_H, PW - M, PH - FOOTER_H);
      sf('normal', 7, C.mutedLt);
      doc.text(`Page ${i} of ${total}`, PW / 2, PH - 5, { align: 'center' });
      doc.text('ninebyninegolf.com', PW - M, PH - 5, { align: 'right' });
    }

    const date = new Date().toISOString().slice(0, 10);
    doc.save(`nine-by-nine-golf-cart-${date}.pdf`);
  }

  // ── Preload all cart item images as base64 data URLs ──
  // jsPDF's addImage needs image data synchronously at draw time, so we
  // fetch + convert every image up-front, then pass the map into buildPDF.
  // Nine by Nine Golf logo — drawn on the FIRST PDF page only.
  var NBN_LOGO_URL =
    'https://www.ninebyninegolf.com/cdn/shop/files/svgviewer-png-output.png?v=1769667395';

  function preloadImages(cart) {
    const urls = new Set();
    cart.items.forEach(item => {
      if (item.image) urls.add(item.image);
    });
    // Also preload the logo (kept as PNG so its transparency is preserved).
    urls.add(NBN_LOGO_URL);

    const loadOne = (url) => new Promise((resolve) => {
      const isLogo = url === NBN_LOGO_URL;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const maxDim = isLogo ? 400 : 160; // logo a bit larger for crispness
          let width = img.width, height = img.height;
          if (width > height && width > maxDim) {
            height = height * (maxDim / width); width = maxDim;
          } else if (height > maxDim) {
            width = width * (maxDim / height); height = maxDim;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!isLogo) {
            // Product thumbs get a white matte; the logo stays transparent.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
          }
          ctx.drawImage(img, 0, 0, width, height);
          // PNG for the logo (transparency), JPEG for product thumbs (smaller).
          const data = isLogo
            ? canvas.toDataURL('image/png')
            : canvas.toDataURL('image/jpeg', 0.82);
          resolve({ url: url, data: data, w: width, h: height, png: isLogo });
        } catch (e) {
          resolve({ url: url, data: null });
        }
      };
      img.onerror = () => resolve({ url: url, data: null });
      // The logo URL already carries a ?v= param; don't append width for it.
      img.src = isLogo
        ? url
        : (url.indexOf('?') === -1 ? (url + '?width=160') : (url + '&width=160'));
    });

    return Promise.all([...urls].map(loadOne)).then(results => {
      const map = {};
      results.forEach(r => { if (r.data) map[r.url] = r; });
      return map;
    });
  }

  function loadJsPDF(cb) {
    if (window.jspdf) return cb();
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload = cb;
    s.onerror = () => alert('Could not load PDF library. Check your internet connection.');
    document.head.appendChild(s);
  }

  function exportCartPDF() {
    const btn = document.getElementById('NbnDownloadPdfBtn');
    const span = btn && btn.querySelector('span');
    if (btn) { btn.disabled = true; if (span) span.textContent = 'Generating\u2026'; }

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        loadJsPDF(() => {
          // Preload images first, then build (images embed synchronously)
          preloadImages(cart)
            .then(imageMap => {
              try {
                buildPDF(cart, imageMap);
              } catch (err) {
                console.error('[NBN PDF]', err);
                alert('Could not generate PDF. Please try again.');
              } finally {
                if (btn) { btn.disabled = false; if (span) span.textContent = 'Download cart (PDF)'; }
              }
            })
            .catch(err => {
              // Even if images fail, still generate the PDF without them
              console.warn('[NBN PDF] Image preload failed, generating without images:', err);
              try {
                buildPDF(cart, {});
              } catch (e) {
                console.error('[NBN PDF]', e);
                alert('Could not generate PDF. Please try again.');
              } finally {
                if (btn) { btn.disabled = false; if (span) span.textContent = 'Download cart (PDF)'; }
              }
            });
        });
      })
      .catch(err => {
        console.error('[NBN PDF] Cart fetch failed:', err);
        alert('Could not load cart data. Please refresh and try again.');
        if (btn) { btn.disabled = false; if (span) span.textContent = 'Download cart (PDF)'; }
      });
  }

  function init() {
    const btn = document.getElementById('NbnDownloadPdfBtn');
    if (btn) btn.addEventListener('click', exportCartPDF);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();